import React, { useEffect } from 'react';
import { withRouter } from 'react-router';
import { Translation, getI18n } from 'react-i18next';
import { Table } from 'antd';

import ListActions from '../../../elements/components/ListActions';
import {
  getColumnSearchProps,
  getColumnDateRangeProps,
} from '../../../elements/components/TableColumnFilters';
import { StatusTag, RoleTag } from '../../../elements/components/Tags';
import { pathTo } from '../../../Routes';
import Format from '../../../../../lib/Format';
import QueryString from '../../../../../lib/QueryString';
import Logger from '../../../../../lib/Logger';
import { mixpanelEventsEnum } from '../../../../../lib/Mixpanel';

const RegistrationCodesList = ({
  component,
  page,
  limit,
  order,
  filter,
  total,
  load,
  remove,
  history,
  partnershipActiveId,
  trackerActions,
  ...props
}) => {
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };

  const handleReset = (clearFilters, dataIndex) => {
    trackerActions(
      mixpanelEventsEnum.ACTIONS_PARTNER.REGISTRATION_CODES_RESET,
      {
        column: dataIndex,
      }
    );
    clearFilters();
    delete filter[dataIndex];
    history.push(
      QueryString.append(props.location.pathname, { order, ...filter })
    );
  };

  const columns = [
    {
      title: getI18n().t('registration_code_code_alias'),
      dataIndex: 'code_alias',
      key: 'code_alias',
      ...getColumnSearchProps(
        'code_alias',
        handleSearch,
        handleReset,
        getI18n().t('registration_code_code_alias'),
        filter
      ),
    },
    {
      title: getI18n().t('registration_code_code'),
      dataIndex: 'code',
      key: 'code',
      ...getColumnSearchProps(
        'code',
        handleSearch,
        handleReset,
        getI18n().t('registration_code_code'),
        filter
      ),
    },
    {
      title: getI18n().t('registration_code_type_user'),
      dataIndex: 'type_user',
      key: 'type_user',
      filters: [
        { text: getI18n().t('registration_code_role_owner'), value: 1 },
        { text: getI18n().t('registration_code_role_admin'), value: 2 },
        { text: getI18n().t('registration_code_role_client'), value: 3 },
        { text: getI18n().t('registration_code_role_default'), value: 4 },
      ],
      filteredValue:
        'type_user' in filter ? filter['type_user'].split(',') : null,
      render: (role) => <RoleTag role={role} />,
    },
    {
      title: getI18n().t('registration_code_expires_at'),
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (date) => Format.date(date),
      defaultSortOrder:
        order === 'expires_at.asc'
          ? 'ascend'
          : order === 'expires_at.desc'
          ? 'descend'
          : null,
      sorter: true,
      ...getColumnDateRangeProps(
        'expires_at',
        handleSearch,
        handleReset,
        filter
      ),
    },
    {
      title: getI18n().t('table_header_status'),
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: getI18n().t('status_enabled'), value: 1 },
        { text: getI18n().t('status_disabled'), value: 2 },
        { text: getI18n().t('status_archived'), value: 3 },
        { text: getI18n().t('status_deleted'), value: 4 },
        { text: getI18n().t('status_pending'), value: 5 },
      ],
      filteredValue: 'status' in filter ? filter['status'].split(',') : null,
      render: (code) => <StatusTag status={code} />,
    },
    {
      title: getI18n().t('table_header_updated_at'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date) => Format.date(date),
      defaultSortOrder:
        order === 'updated_at.asc'
          ? 'ascend'
          : order === 'updated_at.desc'
          ? 'descend'
          : null,
      sorter: true,
      ...getColumnDateRangeProps(
        'updated_at',
        handleSearch,
        handleReset,
        filter
      ),
    },
    {
      title: getI18n().t('table_header_created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => Format.date(date),
      defaultSortOrder:
        order === 'created_at.asc'
          ? 'ascend'
          : order === 'created_at.desc'
          ? 'descend'
          : null,
      sorter: true,
      ...getColumnDateRangeProps(
        'created_at',
        handleSearch,
        handleReset,
        filter
      ),
    },
    {
      title: getI18n().t('table_header_actions'),
      dataIndex: 'id',
      key: 'actions',
      render: (id) => (
        <ListActions
          id={id}
          id_partnership={partnershipActiveId}
          page={page}
          limit={limit}
          order={order}
          filter={filter}
          load={load.bind(this)}
          remove={remove.bind(this)}
          editScreen='RegistrationCodeEditScreen'
          activeDelete={false}
          activeEdit={true}
        />
      ),
    },
  ];

  const pagination = {
    current: page,
    pageSize: limit,
    total: total,
    showSizeChanger: false,
    position: ['bottomCenter'],
  };

  const handleTableChange = (pagination, filters, sorter) => {
    trackerActions(
      mixpanelEventsEnum.ACTIONS_PARTNER.REGISTRATION_CODES_SEARCH,
      {
        sorterField: (Array.isArray(sorter) ? sorter[0] : sorter)?.field,
        sorterOrder: (Array.isArray(sorter) ? sorter[0] : sorter)?.order,
        ...Object.fromEntries(
          Object.entries(filters || {})
            .filter(([k, v]) => Array.isArray(v) && v.length > 0)
            .map(([k, v]) => [k, v.join(',')])
        ),
        paginationCurrent: pagination['current'],
      }
    );

    let path = props.location.pathname;
    const params = {};

    // handle pagination
    if ('current' in pagination && pagination['current']) {
      path = pathTo(component, { page: pagination['current'] });
    }

    // handle sorting
    if ('field' in sorter && 'order' in sorter) {
      if (sorter['field'] && sorter['order']) {
        const order =
          sorter['field'] +
          '.' +
          (sorter['order'] === 'ascend' ? 'asc' : 'desc');
        params['order'] = order;
      }
    }

    // handle filters
    if (filters) {
      for (const key in filters) {
        if (filters[key]) {
          params[key] = filters[key].join(',');
        }
      }
    }

    history.push(QueryString.append(path, params));
  };

  const filterString = JSON.stringify(filter);
  useEffect(() => {
    load(partnershipActiveId, page, limit, order, JSON.parse(filterString));
  }, [partnershipActiveId, page, limit, order, filterString, load]);

  return (
    <Translation>
      {(t) => (
        <Table
          dataSource={props.list}
          columns={columns}
          pagination={page === 1 && total < limit ? false : pagination}
          loading={props.isLoading}
          scroll={{ x: true }}
          onChange={handleTableChange}
        />
      )}
    </Translation>
  );
};

export default withRouter(RegistrationCodesList);

Logger.log('silly', `RegistrationCodesList loaded.`);
